import { Link } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';

type Section = { title: string; paragraphs?: string[]; items?: string[] };

const updated = 'August 2, 2026';

const termsSections: Section[] = [
  {
    title: '1. Acceptance of these Terms',
    paragraphs: [
      'These Terms of Service govern your access to and use of Loopora, including its website, dashboard, integrations, publishing tools, and related services. By creating an account, connecting a third-party account, or using Loopora, you agree to these Terms.',
      'You must be legally able to enter into this agreement and must use Loopora only for lawful business or creator activities.'
    ]
  },
  {
    title: '2. Your account and authorized connections',
    items: [
      'You are responsible for safeguarding your Loopora credentials and all activity performed through your account.',
      'You may connect only social media accounts that you own or are expressly authorized to manage.',
      'Loopora uses authorization methods supplied by third-party platforms. Loopora does not ask for or store your TikTok password.',
      'You are responsible for keeping your account information accurate and for promptly removing access for people who should no longer have it.'
    ]
  },
  {
    title: '3. Content and publishing',
    paragraphs: [
      'You retain ownership of content you upload or publish through Loopora. You grant Loopora a limited license to host, process, format, transmit, and display that content only as needed to provide the service and complete actions you request.',
      'You are responsible for confirming the destination account, caption, media, privacy setting, scheduling time, and other publishing options before submission.'
    ],
    items: [
      'Do not upload content that infringes intellectual property, privacy, publicity, or other rights.',
      'Do not use Loopora to distribute unlawful, deceptive, harmful, abusive, or unauthorized content.',
      'Do not use automation to evade platform safeguards, rate limits, review requirements, or account restrictions.',
      'Third-party platforms may reject, delay, restrict, remove, or modify the visibility of submitted content under their own rules.'
    ]
  },
  {
    title: '4. Third-party platforms',
    paragraphs: [
      'Loopora integrates with independent services such as TikTok. Your use of those services remains subject to their own terms, privacy policies, developer rules, content policies, technical limits, and enforcement decisions. Loopora does not control and is not responsible for a third-party platform’s availability or decisions.',
      'You may disconnect an integration from Loopora. You may also revoke access from the third-party platform where that option is provided.'
    ]
  },
  {
    title: '5. Acceptable use',
    items: [
      'Do not probe, scan, disrupt, overload, reverse engineer, or attempt unauthorized access to Loopora or connected systems.',
      'Do not use Loopora to impersonate another person or organization, send spam, manipulate engagement, or misrepresent the origin of content.',
      'Do not resell or provide access to Loopora except under a separate written agreement.',
      'Do not use the service in a way that violates applicable law, sanctions, export controls, or third-party rights.'
    ]
  },
  {
    title: '6. Availability, changes, and suspension',
    paragraphs: [
      'We may update, improve, limit, or discontinue features to maintain security, reliability, legal compliance, or compatibility with third-party platforms. We may suspend or terminate access when we reasonably believe an account presents a security risk, violates these Terms, infringes rights, or exposes Loopora or others to liability.'
    ]
  },
  {
    title: '7. Disclaimers and limitation of liability',
    paragraphs: [
      'Loopora is provided on an “as is” and “as available” basis to the maximum extent permitted by law. We do not guarantee uninterrupted availability, successful publication, a particular level of reach or engagement, or continued access to any third-party API.',
      'To the maximum extent permitted by law, Loopora will not be liable for indirect, incidental, special, consequential, exemplary, or punitive damages, lost profits, lost revenue, loss of data, platform enforcement, or failed publication arising from use of the service.'
    ]
  },
  {
    title: '8. Changes to these Terms',
    paragraphs: [
      'We may revise these Terms when the service, law, or platform requirements change. The updated date at the top of this page will show when the current version became effective. Continued use after an update means you accept the revised Terms.'
    ]
  },
  {
    title: '9. Contact',
    paragraphs: [
      'Questions about these Terms may be submitted through the support channel available inside the Loopora application.'
    ]
  }
];

const privacySections: Section[] = [
  {
    title: '1. Information we collect',
    paragraphs: ['We collect only information reasonably needed to operate, secure, and improve Loopora. Depending on the features you use, this may include:'],
    items: [
      'Account information, such as your name, email address, organization, workspace membership, and authentication records.',
      'Connected-platform data authorized by you, such as a TikTok user identifier, display name, avatar, granted permissions, access and refresh tokens, and public video metadata available under the permissions you approve.',
      'Content and publishing data, such as media you upload, captions, schedules, destination accounts, privacy selections, publishing status, and error responses.',
      'Technical and security data, such as IP address, browser and device information, timestamps, audit logs, diagnostics, and activity used to prevent abuse and troubleshoot failures.',
      'Support communications and information you choose to provide when requesting assistance.'
    ]
  },
  {
    title: '2. How we use information',
    items: [
      'Authenticate users and provide workspace access.',
      'Connect authorized third-party accounts and perform actions you request.',
      'Display connected profile information so you can confirm the correct destination account.',
      'Upload, schedule, publish, and track content according to your instructions.',
      'Maintain security, prevent fraud or unauthorized access, enforce our Terms, and comply with law.',
      'Monitor reliability, diagnose errors, and improve product performance.'
    ]
  },
  {
    title: '3. TikTok data',
    paragraphs: [
      'When you connect TikTok, TikTok presents its own authorization screen showing the permissions requested by Loopora. Loopora receives only the data and permissions you approve. Loopora does not receive or store your TikTok password.',
      'TikTok data is used to provide the connected-account and publishing features you request. We do not sell TikTok user data. We do not use TikTok data for unrelated advertising profiles. Access is limited to authorized users, service processes, and providers that need it to operate and secure Loopora.'
    ]
  },
  {
    title: '4. How information is shared',
    paragraphs: ['We do not sell your personal information. We may disclose information only in the following circumstances:'],
    items: [
      'To connected platforms when necessary to carry out an action you request.',
      'To infrastructure, storage, security, monitoring, and other service providers acting on our behalf under appropriate contractual restrictions.',
      'To workspace members according to the roles and permissions configured in your organization.',
      'When required by law, legal process, or a valid governmental request, or when reasonably necessary to protect rights, safety, security, and the integrity of the service.',
      'As part of a merger, financing, acquisition, reorganization, or sale of assets, subject to applicable confidentiality and notice obligations.'
    ]
  },
  {
    title: '5. Data retention and deletion',
    paragraphs: [
      'We retain information for as long as necessary to provide the service, maintain security and audit records, comply with legal obligations, resolve disputes, and enforce agreements. Retention periods vary by data type and operational need.',
      'You may disconnect a social account at any time. You may request deletion of your Loopora account and associated personal data through the support channel inside the application. We will process verified requests subject to legal, security, fraud-prevention, and backup-retention requirements.'
    ]
  },
  {
    title: '6. Security',
    paragraphs: [
      'We use administrative, technical, and organizational safeguards designed to protect information, including access controls, encrypted network transport, protected credential storage, and activity logging. No system is completely secure, and we cannot guarantee absolute security.'
    ]
  },
  {
    title: '7. Your choices and rights',
    items: [
      'Review and update available account information.',
      'Disconnect integrations or revoke permissions through Loopora or the applicable third-party platform.',
      'Request access, correction, or deletion where applicable.',
      'Object to or restrict certain processing where required by law.',
      'Lodge a complaint with the appropriate data-protection authority where that right applies.'
    ]
  },
  {
    title: '8. International processing',
    paragraphs: [
      'Loopora and its providers may process information in countries other than the country where you live. Where required, we use appropriate safeguards for cross-border transfers.'
    ]
  },
  {
    title: '9. Children',
    paragraphs: [
      'Loopora is not directed to children under 13, and we do not knowingly collect personal information from children under 13. Users must also satisfy the age requirements of every connected third-party platform.'
    ]
  },
  {
    title: '10. Changes to this Privacy Policy',
    paragraphs: [
      'We may update this Privacy Policy to reflect product, legal, security, or platform changes. The updated date at the top of this page identifies the current version.'
    ]
  },
  {
    title: '11. Contact',
    paragraphs: [
      'Privacy questions and verified data-rights requests may be submitted through the support channel available inside the Loopora application.'
    ]
  }
];

export function LegalPage({ type }: { type: 'terms' | 'privacy' }) {
  const isTerms = type === 'terms';
  const title = isTerms ? 'Terms of Service' : 'Privacy Policy';
  const intro = isTerms
    ? 'These Terms explain the rules that apply when you access or use Loopora.'
    : 'This Privacy Policy explains how Loopora collects, uses, shares, retains, and protects information.';
  const sections = isTerms ? termsSections : privacySections;

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-3 font-bold tracking-tight">
            <img src="/brand/loopora-logo.png?v=20260805" alt="Loopora app icon" className="size-10 rounded-xl object-cover" width="40" height="40" />
            <span>Loopora</span>
          </Link>
          <nav className="flex items-center gap-5 text-sm font-medium text-slate-600">
            <Link to="/terms" className={isTerms ? 'text-blue-700' : 'hover:text-slate-900'}>Terms</Link>
            <Link to="/privacy" className={!isTerms ? 'text-blue-700' : 'hover:text-slate-900'}>Privacy</Link>
            <Link to="/" className="rounded-lg border border-slate-300 px-3 py-2 hover:bg-slate-50">Back to Loopora</Link>
          </nav>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-6 py-12 sm:py-16">
        <div className="mb-10">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700"><ShieldCheck size={14} /> Legal</div>
          <h1 className="text-4xl font-extrabold tracking-tight sm:text-5xl">{title}</h1>
          <p className="mt-4 max-w-2xl text-lg leading-8 text-slate-600">{intro}</p>
          <p className="mt-3 text-sm text-slate-500">Effective and last updated: {updated}</p>
        </div>

        <article className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-10">
          <div className="space-y-10">
            {sections.map((section) => (
              <section key={section.title}>
                <h2 className="text-xl font-bold tracking-tight">{section.title}</h2>
                {section.paragraphs?.map((paragraph) => <p key={paragraph} className="mt-3 leading-7 text-slate-600">{paragraph}</p>)}
                {section.items && (
                  <ul className="mt-4 list-disc space-y-2 pl-6 leading-7 text-slate-600">
                    {section.items.map((item) => <li key={item}>{item}</li>)}
                  </ul>
                )}
              </section>
            ))}
          </div>
        </article>
      </main>

      <footer className="border-t border-slate-200 bg-white">
        <div className="mx-auto flex max-w-5xl flex-col gap-3 px-6 py-8 text-sm text-slate-500 sm:flex-row sm:items-center sm:justify-between">
          <span>© 2026 Loopora. All rights reserved.</span>
          <div className="flex gap-5"><Link to="/terms" className="hover:text-slate-900">Terms of Service</Link><Link to="/privacy" className="hover:text-slate-900">Privacy Policy</Link></div>
        </div>
      </footer>
    </div>
  );
}
